import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import fi from 'date-fns/locale/fi'
import {
  Controller,
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form'
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  TextField
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { CommonFormSectionProps, IJobTitle, IWorkField } from '../../../types'
import { ErrorMessage, FormLabel, LaakariButton } from '../../../components/styled/uiComponents'
import JobResultRow from '../../../components/JobResultRow'

// Helper function to parse date strings in dd.MM.yyyy format (with or without leading zeros)
const parseFlexibleDate = (dateString: string): Date | null => {
  if (!dateString || typeof dateString !== 'string') {
    return null
  }

  // Remove extra whitespace
  const cleaned = dateString.trim()

  // Try to match dd.MM.yyyy or d.M.yyyy format
  const match = cleaned.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
  if (match) {
    const day = parseInt(match[1], 10)
    const month = parseInt(match[2], 10)
    const year = parseInt(match[3], 10)

    // Create date and validate
    const date = new Date(year, month - 1, day)
    if (
      !isNaN(date.getTime()) &&
      date.getDate() === day &&
      date.getMonth() === month - 1 &&
      date.getFullYear() === year
    ) {
      return date
    }
  }

  return null
}

interface Props {
  metaData?: any
  control: Control<any>
  watch: UseFormWatch<any>
  errors: FieldErrors<any>
  skipEmployment?: boolean | (() => void)
  register: UseFormRegister<any>
  setValue?: UseFormSetValue<any>
}
const collator = new Intl.Collator('fi-FI', {
  sensitivity: 'base',
  numeric: true
})
const ORDER = [
  'Hyvinvointialue',
  'Ammatinharjoittaja',
  'Yksityinen (työsuhde)',
  'Yliopisto',
  'Valtio',
  'Muu'
] as const
const orderIndex = new Map(ORDER.map((v, i) => [v.toLowerCase(), i]))

const EmploymentForm: React.FC<Props> = ({
  metaData,
  control,
  watch,
  errors,
  skipEmployment,
  register,
  setValue
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const tyosuhdetoimiala = watch('employment.tyosuhdetoimiala')
  const sector = watch('employment.els_tyosuhdesektori')
  const selectedJob = watch('employment.els_soteorganisaatio')
  const employmentType = watch('employment.tyosuhdeluonne')
  const els_alkupvm = watch('employment.els_alkupvm')
  const sll_tyosuhde_sijaintikunta = watch('employment.sll_tyosuhde_sijaintikunta')
  const job = watch('employment.els_soteorganisaatio')

  const today = format(new Date(), 'yyyy-MM-d')

  useEffect(() => {
    if (employmentType && employmentType !== 'maaraaikainen' && setValue) {
      setValue('employment.els_loppupvm', '')
    }
  }, [employmentType, setValue])
  const navigate = useNavigate()
  const handleSkipEmployment = () => {
    // Use skipEmployment prop if it's a function, otherwise just return
    if (typeof skipEmployment === 'function') {
      skipEmployment()
    }
  }
  const buttonContainer = (
    <LaakariButton onClick={handleSkipEmployment}>Ohita työpaikkatietojen täyttö</LaakariButton>
  )

  return (
    <Grid
      container
      spacing={2}
      sx={{ '& .MuiGrid-item': { paddingTop: '0px !important', paddingBottom: '0px !important' } }}>
      <Grid item xs={12}>
        <Typography variant='h5' color='primary'>
          Työpaikka
        </Typography>
      </Grid>

      <Grid
        item
        xs={12}
        sx={{
          '& .MuiGrid-item': { paddingTop: '0px !important', paddingBottom: '0px !important' }
        }}>
        <p>{`Valitse ensin työnantajasektori`}</p>
      </Grid>
      <Grid container item xs={12}>
        <Grid item xs={12} md={6}>
          <FormLabel>Työnantajasektori</FormLabel>
          <Controller
            name='employment.els_tyosuhdesektori'
            control={control}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                value={value ? JSON.parse(value) : null}
                options={
                  metaData?.sektorit
                    ?.filter((sector: any) => sector.value !== 'Kunta')
                    .sort((a: any, b: any) => {
                      const av = a?.value?.trim().toLowerCase() ?? ''
                      const bv = b?.value?.trim().toLowerCase() ?? ''
                      const ia = orderIndex.get(av) ?? Number.POSITIVE_INFINITY
                      const ib = orderIndex.get(bv) ?? Number.POSITIVE_INFINITY
                      if (ia !== ib) {
                        return ia - ib
                      }
                      if (!av && !bv) {
                        return 0
                      }
                      if (!av) {
                        return 1
                      }
                      if (!bv) {
                        return -1
                      }
                      return av.localeCompare(bv, 'fi-FI', { sensitivity: 'base' })
                    }) || []
                }
                getOptionLabel={(option: any) => (typeof option === 'object' ? option.value : '')}
                isOptionEqualToValue={(option, value) =>
                  option.attributevalue === value.attributevalue
                }
                filterOptions={(options, { inputValue }) => {
                  const cleanedInput = inputValue.replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                  return options.filter(option =>
                    option.value.toLowerCase().includes(cleanedInput.toLowerCase())
                  )
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const cleanedSector = JSON.stringify({
                      attributevalue: newValue.attributevalue,
                      value: newValue.value.trim().replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                    })
                    onChange(cleanedSector)
                    setSearchQuery('')
                    if (setValue) {
                      setValue('employment.els_soteorganisaatio', '')
                    }
                  } else {
                    onChange('')
                    setSearchQuery('')
                    if (setValue) {
                      setValue('employment.els_soteorganisaatio', '')
                    }
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder='Valitse tai kirjoita hakuun...'
                    fullWidth
                    variant='outlined'
                    size='small'
                  />
                )}
                noOptionsText='Ei vaihtoehtoja'
              />
            )}
          />
          <ErrorMessage>{(errors.employment as any)?.els_tyosuhdesektori?.message}</ErrorMessage>
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs={12} md={6}>
          <FormLabel>Työpaikka</FormLabel>
          <Controller
            name='employment.els_soteorganisaatio'
            control={control}
            defaultValue=''
            render={({ field: { onChange, value } }) => {
              let parsedValue = null
              try {
                if (value && typeof value === 'string' && value.trim() !== '') {
                  parsedValue = JSON.parse(value)
                }
              } catch (e) {
                parsedValue = null
              }

              return (
                <Autocomplete
                  key={sector} // Force re-render when sector changes
                  value={parsedValue}
                  options={
                    sector
                      ? (() => {
                          if (!metaData?.tyopaikat) {
                            return []
                          }

                          const allWorkplaces = metaData.tyopaikat

                          try {
                            const sectorData = JSON.parse(sector)
                            const attributevalue = +sectorData.attributevalue

                            const filteredWorkplaces = allWorkplaces
                              .filter((workplace: any) => {
                                const wPlaces = workplace.sll_tietoni_employment_hierarchylevel_1
                                if (wPlaces.includes(',')) {
                                  const nums = wPlaces
                                    .split(',')
                                    .map((s: string) => s.trim())
                                    .filter(Boolean)
                                    .map(Number)
                                    .filter(Number.isFinite)
                                  return nums.includes(attributevalue)
                                }
                                return +wPlaces === +attributevalue
                              })
                              .sort((a: any, b: any) => {
                                const av = a?.els_longname?.trim() ?? null
                                const bv = b?.els_longname?.trim() ?? null
                                if (av == null && bv == null) {
                                  return 0
                                }
                                if (av == null) {
                                  return 1
                                }
                                if (bv == null) {
                                  return -1
                                }
                                return collator.compare(av, bv)
                              })
                            return filteredWorkplaces
                          } catch (e) {
                            return []
                          }
                        })()
                      : []
                  }
                  getOptionLabel={(option: any) =>
                    typeof option === 'object' ? option.els_longname || '' : ''
                  }
                  isOptionEqualToValue={(option, value) => {
                    if (!value || !option) {
                      return false
                    }
                    if (
                      value.els_soteorganisaatiorekisteriid &&
                      option.els_soteorganisaatiorekisteriid
                    ) {
                      return (
                        option.els_soteorganisaatiorekisteriid ===
                        value.els_soteorganisaatiorekisteriid
                      )
                    }
                    return option.els_longname === value.els_longname
                  }}
                  renderOption={(props, option: any) => (
                    <li {...props}>
                      <div>
                        <div>{option.els_longname}</div>
                      </div>
                    </li>
                  )}
                  filterOptions={(options, { inputValue }) => {
                    const cleanedInput = inputValue.replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                    setSearchQuery(cleanedInput)
                    return options.filter(option =>
                      option.els_longname.toLowerCase().includes(cleanedInput.toLowerCase())
                    )
                  }}
                  onChange={(event, newValue) => {
                    if (newValue && typeof newValue === 'object') {
                      const cleanedWorkplace = JSON.stringify({
                        els_longname: newValue.els_longname
                          .trim()
                          .replace(/[^\w\s\-åäöÅÄÖ()]/g, ''),
                        els_soteorganisaatiorekisteriid: newValue.els_soteorganisaatiorekisteriid
                      })
                      onChange(cleanedWorkplace)
                    } else {
                      onChange('')
                    }
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={
                        sector
                          ? 'Valitse tai kirjoita hakuun...'
                          : 'Valitse ensin työnantajasektori'
                      }
                      fullWidth
                      variant='outlined'
                      size='small'
                      disabled={!sector}
                    />
                  )}
                  disabled={!sector}
                  noOptionsText={!sector ? 'Valitse ensin työnantajasektori' : 'Ei vaihtoehtoja'}
                />
              )
            }}
          />
          <ErrorMessage>{(errors.employment as any)?.els_soteorganisaatio?.message}</ErrorMessage>
        </Grid>
      </Grid>

      <Grid container item>
        <Grid item xs={12}>
          <FormLabel>Toimiala</FormLabel>
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name='employment.tyosuhdetoimiala'
            control={control}
            defaultValue=''
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                value={value ? JSON.parse(value) : null}
                options={metaData?.toimialat || []}
                getOptionLabel={(option: any) => (typeof option === 'object' ? option.value : '')}
                isOptionEqualToValue={(option, value) =>
                  option.attributevalue === value.attributevalue
                }
                filterOptions={(options, { inputValue }) => {
                  const cleanedInput = inputValue.replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                  return options.filter(option =>
                    option.value.toLowerCase().includes(cleanedInput.toLowerCase())
                  )
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const cleanedToimiala = JSON.stringify({
                      attributevalue: newValue.attributevalue,
                      value: newValue.value.trim().replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                    })
                    onChange(cleanedToimiala)
                  } else {
                    onChange('')
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={job ? 'Valitse tai kirjoita hakuun...' : 'Valitse ensin työpaikka'}
                    fullWidth
                    variant='outlined'
                    size='small'
                    disabled={!job}
                  />
                )}
                disabled={!job}
                noOptionsText='Ei toimialoja saatavilla'
              />
            )}
          />
          <ErrorMessage>{(errors.employment as any)?.tyosuhdetoimiala?.message}</ErrorMessage>
        </Grid>
      </Grid>

      <Grid container item xs={12}>
        <Grid item xs={12} md={6}>
          <FormLabel>Työpaikan sijainti</FormLabel>
          <Controller
            name='employment.sll_tyosuhde_sijaintikunta'
            control={control}
            defaultValue=''
            render={({ field: { onChange, value } }) => {
              // Find the selected option object based on value (els_koodiid)
              const selectedOption =
                value && metaData?.kunnat
                  ? metaData.kunnat.find((kunta: any) => {
                      const parsedValue =
                        typeof value === 'string' && value.startsWith('{')
                          ? JSON.parse(value).els_koodiid
                          : value
                      return (typeof kunta === 'object' ? kunta.els_koodiid : null) === parsedValue
                    })
                  : null

              return (
                <Autocomplete
                  value={selectedOption || null}
                  options={
                    metaData?.kunnat
                      ?.filter((kunta: any) => typeof kunta === 'object')
                      .sort((a: any, b: any) => {
                        const aLabel = a.els_koodinnimi || a.name || a.value || ''
                        const bLabel = b.els_koodinnimi || b.name || b.value || ''
                        if (aLabel == null && bLabel == null) {
                          return 0
                        }
                        if (aLabel == null) {
                          return 1
                        } // nulls last
                        if (bLabel == null) {
                          return -1
                        }
                        return collator.compare(aLabel, bLabel) // ASC; swap to (bv, av) for DESC
                      }) || []
                  }
                  getOptionLabel={(option: any) =>
                    typeof option === 'object'
                      ? option.els_koodinnimi || option.name || option.value || ''
                      : ''
                  }
                  isOptionEqualToValue={(option, val) => {
                    if (!option || !val) {
                      return false
                    }
                    return (
                      (typeof option === 'object' ? option.els_koodiid : null) ===
                      (typeof val === 'object' ? val.els_koodiid : null)
                    )
                  }}
                  filterOptions={(options, { inputValue }) => {
                    // Clean search input and filter options based on cleaned input
                    const cleanedInput = inputValue.replace(/[^\w\s\-åäöÅÄÖ]/g, '')
                    return options.filter((option: any) => {
                      const label = option.els_koodinnimi || option.name || option.value || ''
                      return label.toLowerCase().includes(cleanedInput.toLowerCase())
                    })
                  }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      // Clean and set the selected job title in the form
                      const cleanedJobTitle = JSON.stringify({
                        els_koodiid: newValue.els_koodiid,
                        els_koodinnimi: newValue.els_koodinnimi
                          .trim()
                          .replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                      })
                      onChange(cleanedJobTitle)
                    } else {
                      // Clear the sll_tyosuhde_sijaintikunta when selection is cleared
                      onChange('')
                    }
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={
                        tyosuhdetoimiala
                          ? 'Valitse tai kirjoita hakuun...'
                          : 'Valitse ensin toimiala'
                      }
                      fullWidth
                      variant='outlined'
                      size='small'
                      disabled={!tyosuhdetoimiala}
                    />
                  )}
                  disabled={!tyosuhdetoimiala}
                  noOptionsText='Ei kaupunkeja saatavilla'
                />
              )
            }}
          />
          <ErrorMessage>
            {(errors.employment as any)?.sll_tyosuhde_sijaintikunta?.message}
          </ErrorMessage>
        </Grid>
      </Grid>
      <Grid container item xs={12}>
        <Grid item xs={12} md={6}>
          <FormLabel>Ammattinimike</FormLabel>
          <Controller
            control={control}
            name='employment.ammattinimike'
            defaultValue=''
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                value={value ? JSON.parse(value) : null}
                options={metaData?.ammattinimikkeet || []}
                getOptionLabel={(option: any) =>
                  typeof option === 'object' ? option.els_koodinnimi : ''
                }
                isOptionEqualToValue={(option, value) => option.els_koodiid === value.els_koodiid}
                filterOptions={(options, { inputValue }) => {
                  // Clean search input and filter options based on cleaned input
                  const cleanedInput = inputValue.replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                  return options.filter(option =>
                    option.els_koodinnimi.toLowerCase().includes(cleanedInput.toLowerCase())
                  )
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    // Clean and set the selected job title in the form
                    const cleanedJobTitle = JSON.stringify({
                      els_koodiid: newValue.els_koodiid,
                      els_koodinnimi: newValue.els_koodinnimi
                        .trim()
                        .replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                    })
                    onChange(cleanedJobTitle)
                  } else {
                    // Clear the job title when selection is cleared
                    onChange('')
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={
                      sll_tyosuhde_sijaintikunta
                        ? 'Valitse tai kirjoita hakuun...'
                        : 'Valitse ensin työpaikan sijainti'
                    }
                    fullWidth
                    variant='outlined'
                    size='small'
                    disabled={!sll_tyosuhde_sijaintikunta}
                  />
                )}
                disabled={!sll_tyosuhde_sijaintikunta}
                noOptionsText='Ei ammattinimikkeitä saatavilla'
              />
            )}
          />
          <ErrorMessage>{(errors.employment as any)?.ammattinimike?.message}</ErrorMessage>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <FormLabel>Onko tämä päätoiminen työpaikkasi?</FormLabel>
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={'employment.els_paatoimi'}
          control={control}
          defaultValue='yes'
          render={({ field: { onChange, value } }) => (
            <RadioGroup onChange={e => onChange(e.target.value)} value={value}>
              <FormControlLabel value='yes' control={<Radio />} label='Kyllä' />
              <FormControlLabel value='no' control={<Radio />} label='Ei' />
            </RadioGroup>
          )}
        />
        <ErrorMessage>{(errors.employment as any)?.els_paatoimi?.message}</ErrorMessage>
      </Grid>
      <Grid container item xs={12}>
        <Grid item xs={12} md={6}>
          <FormLabel>Työajan jakautuminen</FormLabel>
          <Controller
            control={control}
            name='employment.sll_tyoaika'
            defaultValue='921950000'
            render={({ field }) => (
              <RadioGroup {...field}>
                <FormControlLabel value='921950000' control={<Radio />} label='Kokoaikainen' />
                <FormControlLabel value='921950001' control={<Radio />} label='Osa-aikainen' />
              </RadioGroup>
            )}
          />
          <ErrorMessage>{(errors.employment as any)?.sll_tyoaika?.message}</ErrorMessage>
        </Grid>
      </Grid>
      <Grid container item xs={12}>
        <Grid item xs={12} md={6}>
          <FormLabel>Työsuhteen luonne</FormLabel>
          <Controller
            control={control}
            name='employment.tyosuhdeluonne'
            defaultValue='toistaiseksi_voimassa'
            render={({ field }) => (
              <RadioGroup {...field}>
                <FormControlLabel
                  value='toistaiseksi_voimassa'
                  control={<Radio />}
                  label='Toistaiseksi voimassa oleva'
                />
                <FormControlLabel value='maaraaikainen' control={<Radio />} label='Määräaikainen' />
              </RadioGroup>
            )}
          />
          <ErrorMessage>{(errors.employment as any)?.tyosuhdeluonne?.message}</ErrorMessage>
        </Grid>
      </Grid>

      {
        <>
          <Grid container item xs={12}>
            <Grid item xs={12} md={6}>
              <FormLabel>Työsuhteen alkupäivä</FormLabel>
              <Controller
                control={control}
                name='employment.els_alkupvm'
                defaultValue=''
                render={({ field: { onChange, value } }) => {
                  // Only parse if value is a valid YYYY-MM-DD format string
                  let parsedDate: Date | null = null
                  if (value && typeof value === 'string') {
                    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
                    if (dateRegex.test(value)) {
                      try {
                        const date = new Date(value)
                        // Validate that the date is actually valid
                        if (!isNaN(date.getTime())) {
                          const [year, month, day] = value.split('-').map(Number)
                          if (
                            date.getFullYear() === year &&
                            date.getMonth() === month - 1 &&
                            date.getDate() === day
                          ) {
                            parsedDate = date
                          }
                        }
                      } catch (e) {
                        // Invalid date, keep parsedDate as null
                      }
                    }
                  }

                  return (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                      <DatePicker
                        label=''
                        value={parsedDate}
                        onChange={newValue => {
                          // Only format if date is complete and reasonable (year >= 1900)
                          // This prevents formatting incomplete dates during keyboard input
                          if (
                            newValue &&
                            newValue instanceof Date &&
                            !isNaN(newValue.getTime()) &&
                            newValue.getFullYear() >= 1900
                          ) {
                            try {
                              onChange(format(newValue, 'yyyy-MM-dd'))
                            } catch (e) {
                              // Don't update form value if formatting fails during typing
                            }
                          } else if (!newValue) {
                            // Only clear if explicitly cleared (null/undefined)
                            onChange('')
                          }
                          // If date is incomplete (year < 1900), don't update form value
                          // This allows user to continue typing without interruption
                        }}
                        onAccept={newValue => {
                          // Handle manual keyboard input - always format on accept
                          if (newValue && newValue instanceof Date && !isNaN(newValue.getTime())) {
                            try {
                              onChange(format(newValue, 'yyyy-MM-dd'))
                            } catch (e) {
                              onChange('')
                            }
                          } else if (!newValue) {
                            onChange('')
                          }
                        }}
                        inputFormat='dd.MM.yyyy'
                        mask=''
                        disableMaskedInput
                        renderInput={params => (
                          <TextField
                            {...params}
                            placeholder='p.k.vvvv tai pp.kk.vvvv'
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            required
                            inputProps={{
                              ...params.inputProps,
                              readOnly: false,
                              placeholder: 'p.k.vvvv tai pp.kk.vvvv'
                            }}
                            onBlur={e => {
                              // Parse manually typed dates when user leaves the field
                              const typedValue = e.target.value
                              if (typedValue) {
                                const parsedDate = parseFlexibleDate(typedValue)
                                if (parsedDate) {
                                  onChange(format(parsedDate, 'yyyy-MM-dd'))
                                }
                              }
                              // Call original onBlur if it exists
                              if (params.inputProps?.onBlur) {
                                params.inputProps.onBlur(e)
                              }
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  )
                }}
              />
              <ErrorMessage>{(errors.employment as any)?.els_alkupvm?.message}</ErrorMessage>
            </Grid>
          </Grid>

          {employmentType === 'maaraaikainen' && (
            <Grid container item xs={12}>
              <Grid item xs={12} md={6}>
                <FormLabel>Työsuhteen loppupäivä</FormLabel>
                <Controller
                  control={control}
                  name='employment.els_loppupvm'
                  defaultValue=''
                  render={({ field: { onChange, value } }) => {
                    // Only parse if value is a valid YYYY-MM-DD format string
                    let parsedDate: Date | null = null
                    if (value && typeof value === 'string') {
                      const dateRegex = /^\d{4}-\d{2}-\d{2}$/
                      if (dateRegex.test(value)) {
                        try {
                          const date = new Date(value)
                          // Validate that the date is actually valid
                          if (!isNaN(date.getTime())) {
                            const [year, month, day] = value.split('-').map(Number)
                            if (
                              date.getFullYear() === year &&
                              date.getMonth() === month - 1 &&
                              date.getDate() === day
                            ) {
                              parsedDate = date
                            }
                          }
                        } catch (e) {
                          // Invalid date, keep parsedDate as null
                        }
                      }
                    }

                    return (
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                        <DatePicker
                          label=''
                          value={parsedDate}
                          onChange={newValue => {
                            // Only format if date is complete and reasonable (year >= 1900)
                            // This prevents formatting incomplete dates during keyboard input
                            if (
                              newValue &&
                              newValue instanceof Date &&
                              !isNaN(newValue.getTime()) &&
                              newValue.getFullYear() >= 1900
                            ) {
                              try {
                                onChange(format(newValue, 'yyyy-MM-dd'))
                              } catch (e) {
                                // Don't update form value if formatting fails during typing
                              }
                            } else if (!newValue) {
                              // Only clear if explicitly cleared (null/undefined)
                              onChange('')
                            }
                            // If date is incomplete (year < 1900), don't update form value
                            // This allows user to continue typing without interruption
                          }}
                          onAccept={newValue => {
                            // Handle manual keyboard input - always format on accept
                            if (
                              newValue &&
                              newValue instanceof Date &&
                              !isNaN(newValue.getTime())
                            ) {
                              try {
                                onChange(format(newValue, 'yyyy-MM-dd'))
                              } catch (e) {
                                onChange('')
                              }
                            } else if (!newValue) {
                              onChange('')
                            }
                          }}
                          inputFormat='dd.MM.yyyy'
                          mask=''
                          disableMaskedInput
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder='p.k.vvvv tai pp.kk.vvvv'
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              required
                              inputProps={{
                                ...params.inputProps,
                                readOnly: false,
                                placeholder: 'p.k.vvvv tai pp.kk.vvvv'
                              }}
                              onBlur={e => {
                                // Parse manually typed dates when user leaves the field
                                const typedValue = e.target.value
                                if (typedValue) {
                                  const parsedDate = parseFlexibleDate(typedValue)
                                  if (parsedDate) {
                                    onChange(format(parsedDate, 'yyyy-MM-dd'))
                                  }
                                }
                                // Call original onBlur if it exists
                                if (params.inputProps?.onBlur) {
                                  params.inputProps.onBlur(e)
                                }
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    )
                  }}
                />
                <ErrorMessage>{(errors.employment as any)?.els_loppupvm?.message}</ErrorMessage>
              </Grid>
            </Grid>
          )}
          {skipEmployment && (
            <Grid container item xs={12} sx={{ marginTop: '2vh' }}>
              <Grid item>
                <LaakariButton variant='outlined' onClick={handleSkipEmployment}>
                  Ohita työpaikkatietojen täyttö
                </LaakariButton>
              </Grid>
            </Grid>
          )}
        </>
      }
    </Grid>
  )
}

export default EmploymentForm
